import React, { useState } from "react";
// import JsonCollapse from "../Builder/Components/JsonCollapse";
import { Form, InputGroup, Tab, Tabs } from "react-bootstrap";
import Input from "../Builder/nodes/Logic/Inputs/Input";
import {
  useCreateContactSetMutation,
  useGetContactSetsQuery,
} from "../store/slices/contactsSlice";
import {
  useCreateReportingTemplateMutation,
  useCreateReportingTopicMutation,
  useGetReportingTemplatesQuery,
  useGetReportingTopicsQuery,
} from "../store/slices/reportingSlice";
import InputGroupText from "react-bootstrap/esm/InputGroupText";
import { shortUUID } from "../helpers";
import SelectApiConnection from "../Monitoring/SelectApiConnection";
import ApiConnection from "../Common/DataImportModal/ApiConnection";
import { useUpdateApiConnectionMutation } from "../store/slices/settingsSlice";
import SolutionImportCheckContactField from "./SolutionImportCheckContactField";
import SolutionImportCheckReportingTemplate from "./SolutionImportCheckReportingTemplate";
import { toast } from "react-toastify";
import FullScreenLoadingIndicator from "../Common/FullScreenLoadingIndicator";

function ImportFieldInfo({ info }: { info: any }) {
  if (
    !info ||
    info.length === 0 ||
    !info.some((i: any) => i.Help && i.Help.length > 0)
  ) {
    return null;
  }
  return (
    <div className="mt-2">
      {info?.map((comp: any, index: number) => (
        <div key={index}>
          {comp.Help?.map(
            (
              line: {
                EventType: string;
                RuleLabel: string;
                ActionKind: string;
                MessageLabel: string;
                VariableLabel: string;
                WorkflowLabel: string;
                InComponentKind: string;
                InComponentUuid: string;
              },
              lineIndex: number
            ) => (
              <div key={lineIndex}>
                This field is used in{" "}
                <span className="fw-bold">{comp.SolutionComponentLabel}</span>{" "}
                component in {line.InComponentKind}{" "}
                {line.ActionKind && (
                  <span className="fw-bold">{line.ActionKind}</span>
                )}{" "}
                in{" "}
                {line.WorkflowLabel && (
                  <span className="fw-bold">{line.WorkflowLabel}</span>
                )}{" "}
                when{" "}
                {line.EventType && (
                  <>
                    Event: <span className="fw-bold">{line.EventType}</span>
                  </>
                )}
                {line.MessageLabel && (
                  <>
                    Message:
                    <span className="fw-bold"> ${line.MessageLabel}</span>
                  </>
                )}
              </div>
            )
          )}
        </div>
      ))}
    </div>
  );
}

export default function SolutionImportCheckFields({
  importedJSON,
  setImportedJSON,
  manualFields,
  setManualFields,
  firstTab,
  end,
}: {
  importedJSON: any;
  setImportedJSON: React.Dispatch<React.SetStateAction<any>>;
  manualFields: string[];
  setManualFields: React.Dispatch<React.SetStateAction<string[]>>;
  firstTab?: React.ReactNode;
  end?: React.ReactNode;
}) {
  const { data: { data: reportingTemplates } = { data: [] } } =
    useGetReportingTemplatesQuery({ pagination: { page: 0, perPage: 100 } });
  const { data: { data: reportingTopics } = { data: [] } } =
    useGetReportingTopicsQuery({ pagination: { page: 0, perPage: 100 } });
  const { data: { contactSets } = { contactSets: [] } } =
    useGetContactSetsQuery({
      page: 0,
      perPage: 100,
    });

  const [createReportingTemplate] = useCreateReportingTemplateMutation();
  const [createReportingTopic] = useCreateReportingTopicMutation();
  const [createContactSet] = useCreateContactSetMutation();
  const [updateApiConnection, { isLoading: isApiLoading }] =
    useUpdateApiConnectionMutation();

  const [apiProfiles, setApiProfiles] = useState<any>({});

  return (
    <div className="">
      <FullScreenLoadingIndicator loading={isApiLoading} />
      <Tabs mountOnEnter>
        {firstTab}
        {Object.entries(importedJSON.Bag.External.ContactField || {}).length >
          0 && (
          <Tab eventKey="fields" title="Contact fields">
            <div className="">
              <div className="bg-purple-gradient p-2 rounded-2">
                <h2>Contact Fields</h2>
                <div className="alert alert-light">
                  We have detected that the solution requires the following
                  contact fields to function properly. We will use these fields
                  {importedJSON.Item.ContactFields.find(
                    (f: any) => f.is_sms || f.is_email || f.is_voice
                  )
                    ? " in the communication processes"
                    : ""}
                  {importedJSON.Item.ContactFields.find(
                    (f: any) => f.is_sms || f.is_email || f.is_voice
                  ) &&
                  importedJSON.Item.ContactFields.find(
                    (f: any) => !f.is_sms && !f.is_email && !f.is_voice
                  )
                    ? " and "
                    : ""}
                  {importedJSON.Item.ContactFields.find(
                    (f: any) => !f.is_sms && !f.is_email && !f.is_voice
                  )
                    ? " for grouping recipients and storing additional information"
                    : ""}
                  . Please review and adjust as necessary.
                </div>
                {Object.entries(
                  importedJSON.Bag.External.ContactField || {}
                ).map(([key, value]: [string, any]) => (
                  <div
                    key={key}
                    className="mb-3 p-3 bg-purple-gradient-darker-reverse rounded-3"
                  >
                    <SolutionImportCheckContactField
                      fieldkey={key}
                      importedJSON={importedJSON}
                      setImportedJSON={setImportedJSON}
                      value={value}
                      manualFields={manualFields}
                      setManualFields={setManualFields}
                    />
                    <ImportFieldInfo
                      info={
                        importedJSON.Item.ContactFields.find(
                          (f: any) => f.uuid === key
                        )?.info
                      }
                    />
                  </div>
                ))}
              </div>
            </div>
          </Tab>
        )}
        {Object.entries(importedJSON.Bag.External.ContactSet || {}).length >
          0 && (
          <Tab eventKey="contact-sets" title="Contact Sets">
            <div className="">
              <div className="bg-purple-gradient p-2 rounded-2">
                <h2>Contact Groups</h2>
                <div className="alert alert-light">
                  We have detected that the solution requires the following
                  contact groups to function properly. We will use these groups
                  in the communication processes and for grouping recipients.
                  Please review and adjust as necessary.
                </div>
                {Object.entries(importedJSON.Bag.External.ContactSet || {}).map(
                  ([key, value]: [string, any]) => (
                    <div
                      key={key}
                      className="mb-3 p-3 bg-purple-gradient-darker-reverse rounded-3"
                    >
                      <InputGroup>
                        <InputGroupText className="col-3">
                          {
                            importedJSON.Item.ContactGroups.find(
                              (f: any) => f.uuid === key
                            )?.label
                          }
                        </InputGroupText>
                        {!value && !manualFields.includes(key) ? (
                          <InputGroupText
                            className="text-muted col"
                            style={{ height: 40 }}
                          >
                            This field will be created automatically upon
                            deployment. Or you can{" "}
                            <button
                              className="btn btn-link p-0 align-baseline ms-1"
                              onClick={() => {
                                setManualFields((prev) => [...prev, key]);
                              }}
                            >
                              manage manually
                            </button>
                            .
                          </InputGroupText>
                        ) : manualFields.includes(key) ? (
                          <div className="col ms-n1">
                            <div className="mb-2">
                              <Form.Check
                                inline
                                type="radio"
                                id={`${key}-contactset-select-existing`}
                                label="Select existing group"
                                name={`${key}-contactset-choice`}
                                checked={
                                  value !== null &&
                                  value !== undefined &&
                                  !value.startsWith("NEW:")
                                }
                                onChange={() => {
                                  setImportedJSON((prev: any) => {
                                    const updated = { ...prev };
                                    updated.Bag.External.ContactSet[key] = "";
                                    return updated;
                                  });
                                }}
                              />
                              <Form.Check
                                inline
                                type="radio"
                                id={`${key}-contactset-create-new`}
                                label="Create new group"
                                name={`${key}-contactset-choice`}
                                checked={
                                  value === null ||
                                  value === undefined ||
                                  value.startsWith("NEW:")
                                }
                                onChange={() => {
                                  setImportedJSON((prev: any) => {
                                    const updated = { ...prev };
                                    updated.Bag.External.ContactSet[key] =
                                      "NEW:";
                                    return updated;
                                  });
                                }}
                              />
                            </div>
                            {value === "" ||
                            (value && !value.startsWith("NEW:")) ? (
                              contactSets && (
                                <Input
                                  placeholder="Select an existing contact group"
                                  inputType="select"
                                  value={[value]}
                                  onChange={([v]: any) => {
                                    setImportedJSON((prev: any) => {
                                      const updated = { ...prev };
                                      updated.Bag.External.ContactSet[key] = v;
                                      return updated;
                                    });
                                  }}
                                  options={contactSets?.map((cf: any) => ({
                                    value: cf.uuid,
                                    label: cf.label,
                                  }))}
                                />
                              )
                            ) : (
                              <Form.Control
                                type="text"
                                placeholder="Enter a name for the new contact group"
                                value={
                                  value?.startsWith("NEW:")
                                    ? value.substring(4)
                                    : ""
                                }
                                onChange={(e) => {
                                  const newValue = e.target.value.trim();
                                  setImportedJSON((prev: any) => {
                                    const updated = { ...prev };
                                    updated.Bag.External.ContactSet[key] =
                                      newValue ? `NEW:${newValue}` : "NEW:";
                                    return updated;
                                  });
                                }}
                              />
                            )}
                          </div>
                        ) : (
                          <div className="col ms-n1">
                            {contactSets && (
                              <Input
                                placeholder="Select an existing contact group"
                                inputType="select"
                                value={[value]}
                                onChange={([v]: any) => {
                                  setImportedJSON((prev: any) => {
                                    const updated = { ...prev };
                                    updated.Bag.External.ContactSet[key] = v;
                                    return updated;
                                  });
                                }}
                                options={contactSets?.map((cf: any) => ({
                                  value: cf.uuid,
                                  label: cf.label,
                                }))}
                              />
                            )}
                          </div>
                        )}
                        <div className="col-auto d-flex justify-content-end align-items-center">
                          <Form.Check
                            id={`contact-set-manual-${key}`}
                            type="switch"
                            label="Manage manually"
                            checked={manualFields.includes(key)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setManualFields((prev) => [...prev, key]);
                              } else {
                                setManualFields((prev) =>
                                  prev.filter((f) => f !== key)
                                );

                                // remove values
                                setImportedJSON((prev: any) => {
                                  const updated = { ...prev };
                                  updated.Bag.External.ContactSet[key] = "";

                                  return updated;
                                });
                              }
                            }}
                            className="ms-3"
                          />
                        </div>
                      </InputGroup>
                      <ImportFieldInfo
                        info={
                          importedJSON.Item.ContactGroups.find(
                            (f: any) => f.uuid === key
                          ).info
                        }
                      />
                      {/* <div className="overflow-auto">
                        <div>
                          <JsonCollapse
                            defaulPlainText
                            label="needed"
                            json={importedJSON.Item.ContactGroups.find(
                              (f: any) => f.uuid === key
                            )}
                          />
                        </div>
                        <div>
                          <JsonCollapse
                            defaulPlainText
                            label="selected"
                            json={contactSets?.find(
                              (f: any) => f.uuid === value
                            )}
                          />
                        </div>
                      </div> */}
                    </div>
                  )
                )}{" "}
              </div>
            </div>
          </Tab>
        )}
        {Object.entries(importedJSON.Bag.External.ReportingTemplate || {})
          .length > 0 && (
          <Tab eventKey="reporting-templates" title="Reporting Templates">
            <div className="">
              <div className="bg-purple-gradient p-2 rounded-2">
                <h2>Reporting Templates</h2>
                <div className="alert alert-light">
                  We have detected that the solution requires certain reporting
                  templates to function properly. We will use the info from the
                  following templates to collect process and reporting data.
                  Please review and adjust as necessary.
                </div>
                {Object.entries(
                  importedJSON.Bag.External.ReportingTemplate || {}
                ).map(([key, value]: [string, any]) => (
                  <div
                    key={key}
                    className="mb-3 p-3 bg-purple-gradient-darker-reverse rounded-3"
                  >
                    <SolutionImportCheckReportingTemplate
                      importedJSON={importedJSON}
                      setImportedJSON={setImportedJSON}
                      templateKey={key}
                      value={value}
                      manualFields={manualFields}
                      setManualFields={setManualFields}
                    />
                    <ImportFieldInfo
                      info={
                        importedJSON.Item.ReportingTemplates.find(
                          (f: any) => f.uuid === key
                        ).info
                      }
                    />
                  </div>
                ))}
              </div>
            </div>
          </Tab>
        )}
        {Object.entries(importedJSON.Bag.External.Topic || {}).length > 0 && (
          <Tab eventKey="reporting-topics" title="Reporting Topics">
            <div className="">
              <div className="bg-purple-gradient p-2 rounded-2">
                <h2>Reporting Topics</h2>
                <div className="alert alert-light">
                  We have detected that the solution requires certain reporting
                  topics to function properly. We will use the info from the
                  following topics to organize process and reporting data.
                  Please review and adjust as necessary.
                </div>
                {Object.entries(importedJSON.Bag.External.Topic || {}).map(
                  ([key, value]: [string, any]) => (
                    <div
                      key={key}
                      className="mb-3 p-3 bg-purple-gradient-darker-reverse rounded-3"
                    >
                      <InputGroup>
                        <InputGroupText className="col-3">
                          {
                            importedJSON.Item.ReportingTopics.find(
                              (f: any) => f.uuid === key
                            )?.label
                          }
                        </InputGroupText>
                        {!value && !manualFields.includes(key) ? (
                          <InputGroupText
                            className="text-muted col"
                            style={{ height: 40 }}
                          >
                            This topic will be created automatically upon
                            deployment. Or you can{" "}
                            <button
                              className="btn btn-link p-0 align-baseline ms-1"
                              onClick={() => {
                                setManualFields((prev) => [...prev, key]);
                              }}
                            >
                              manage manually
                            </button>
                            .
                          </InputGroupText>
                        ) : manualFields.includes(key) ? (
                          <div className="col ms-n1">
                            <div className="mb-2">
                              <Form.Check
                                inline
                                type="radio"
                                id={`${key}-topic-select-existing`}
                                label="Select existing topic"
                                name={`${key}-topic-choice`}
                                checked={
                                  value !== null &&
                                  value !== undefined &&
                                  !value.startsWith("NEW:")
                                }
                                onChange={() => {
                                  setImportedJSON((prev: any) => {
                                    const updated = { ...prev };
                                    updated.Bag.External.Topic[key] = "";
                                    return updated;
                                  });
                                }}
                              />
                              <Form.Check
                                inline
                                type="radio"
                                id={`${key}-topic-create-new`}
                                label="Create new topic"
                                name={`${key}-topic-choice`}
                                checked={
                                  value === null ||
                                  value === undefined ||
                                  value.startsWith("NEW:")
                                }
                                onChange={() => {
                                  setImportedJSON((prev: any) => {
                                    const updated = { ...prev };
                                    updated.Bag.External.Topic[key] = "NEW:";
                                    return updated;
                                  });
                                }}
                              />
                            </div>
                            {value === "" ||
                            (value && !value.startsWith("NEW:")) ? (
                              reportingTopics && (
                                <Input
                                  placeholder="Select an existing topic"
                                  inputType="select"
                                  value={[value]}
                                  onChange={([v]: any) => {
                                    setImportedJSON((prev: any) => {
                                      const updated = { ...prev };
                                      updated.Bag.External.Topic[key] = v;
                                      return updated;
                                    });
                                  }}
                                  options={reportingTopics?.map((cf: any) => ({
                                    value: cf.uuid,
                                    label: cf.label,
                                  }))}
                                />
                              )
                            ) : (
                              <Form.Control
                                type="text"
                                placeholder="Enter a name for the new topic"
                                value={
                                  value?.startsWith("NEW:")
                                    ? value.substring(4)
                                    : ""
                                }
                                onChange={(e) => {
                                  const newValue = e.target.value.trim();
                                  setImportedJSON((prev: any) => {
                                    const updated = { ...prev };
                                    updated.Bag.External.Topic[key] = newValue
                                      ? `NEW:${newValue}`
                                      : "NEW:";
                                    return updated;
                                  });
                                }}
                              />
                            )}
                          </div>
                        ) : (
                          <div className="col ms-n1">
                            {reportingTopics && (
                              <Input
                                placeholder="Select an existing topic"
                                inputType="select"
                                value={[value]}
                                onChange={([v]: any) => {
                                  setImportedJSON((prev: any) => {
                                    const updated = { ...prev };
                                    updated.Bag.External.Topic[key] = v;
                                    return updated;
                                  });
                                }}
                                options={reportingTopics?.map((cf: any) => ({
                                  value: cf.uuid,
                                  label: cf.label,
                                }))}
                              />
                            )}
                          </div>
                        )}
                        <div className="col-auto d-flex justify-content-end align-items-center">
                          <Form.Check
                            id={`reporting-topic-manual-${key}`}
                            type="switch"
                            label="Manage manually"
                            checked={manualFields.includes(key)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setManualFields((prev) => [...prev, key]);
                              } else {
                                setManualFields((prev) =>
                                  prev.filter((f) => f !== key)
                                );

                                // remove values

                                setImportedJSON((prev: any) => {
                                  const updated = { ...prev };
                                  updated.Bag.External.Topic[key] = "";

                                  return updated;
                                });
                              }
                            }}
                            className="ms-3"
                          />
                        </div>
                      </InputGroup>
                      <ImportFieldInfo
                        info={
                          importedJSON.Item.ReportingTopics.find(
                            (f: any) => f.uuid === key
                          ).info
                        }
                      />
                      {/* <div className="overflow-auto">
                        <div>
                          <JsonCollapse
                            defaulPlainText
                            label="needed"
                            json={importedJSON.Item.ReportingTopics.find(
                              (f: any) => f.uuid === key
                            )}
                          />
                        </div>
                        <div>
                          <JsonCollapse
                            defaulPlainText
                            label="selected"
                            json={reportingTopics?.find(
                              (f: any) => f.uuid === value
                            )}
                          />
                        </div>
                      </div> */}
                    </div>
                  )
                )}{" "}
              </div>
            </div>
          </Tab>
        )}
        {Object.entries(importedJSON.Bag.External.ApiConnection || {}).length >
          0 && (
          <Tab eventKey="api-connection" title="API Connection">
            <div className="">
              <div className="bg-purple-gradient p-2 rounded-2">
                <h2>API Connection</h2>
                {Object.entries(
                  importedJSON.Bag.External.ApiConnection || {}
                ).map(([key, value]: [string, any]) => (
                  <div key={key} className="mb-3">
                    <div className="d-flex justify-content-end mb-2">
                      <Form.Check
                        id={`api-connection-manual-${key}`}
                        type="switch"
                        label="Manage manually"
                        checked={manualFields.includes(key)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setManualFields((prev) => [...prev, key]);
                          } else {
                            setManualFields((prev) =>
                              prev.filter((f) => f !== key)
                            );

                            // remove values

                            setImportedJSON((prev: any) => {
                              const updated = { ...prev };
                              updated.Bag.External.ApiConnection[key] = "";

                              return updated;
                            });
                          }
                        }}
                        className="ms-3"
                      />
                    </div>
                    {!value && !manualFields.includes(key) ? (
                      <InputGroupText className="text-muted">
                        This API Connection will be created automatically upon
                        deployment. Or you can{" "}
                        <button
                          className="btn btn-link p-0 align-baseline ms-1"
                          onClick={() => {
                            setManualFields((prev) => [...prev, key]);
                          }}
                        >
                          manage manually
                        </button>
                        .
                      </InputGroupText>
                    ) : (
                      <div key={value}>
                        Please select an existing API Connection or create a new
                        one:
                        <Tabs defaultActiveKey="select" className="mb-3">
                          <Tab eventKey="select" title="Select Existing">
                            <h5>Select an API Connection</h5>
                            <SelectApiConnection
                              selected={value}
                              onSelect={(api) => {
                                setImportedJSON((prev: any) => {
                                  const updated = { ...prev };
                                  updated.Bag.External.ApiConnection[key] =
                                    api?.Uuid;
                                  return updated;
                                });
                              }}
                            />
                          </Tab>
                          <Tab eventKey="create" title="Create New">
                            <h5>Create a New API Connection</h5>
                            <ApiConnection
                              api={apiProfiles[key] || {}}
                              onChange={(Profile) => {
                                const apiConnection = {
                                  Status: "production",
                                  Kind: "third-party",
                                  Label: key + " API Connection",
                                  Profile,
                                };
                                setApiProfiles((prev: any) => ({
                                  ...prev,
                                  [key]: Profile,
                                }));
                              }}
                            />
                            <button
                              className="btn btn-primary mt-3"
                              disabled={!apiProfiles[key]}
                              onClick={async () => {
                                const res: any = await updateApiConnection({
                                  Status: "production",
                                  Kind: "third-party",
                                  Label: key + " API Connection",
                                  Profile: apiProfiles[key],
                                });
                                if (res?.data?.Uuid) {
                                  toast.success("API Connection created");
                                  setImportedJSON((prev: any) => {
                                    const updated = { ...prev };
                                    updated.Bag.External.ApiConnection[key] =
                                      res.data.Uuid;
                                    return updated;
                                  });
                                  setApiProfiles((prev: any) => ({
                                    ...prev,
                                    [key]: null,
                                  }));
                                }
                              }}
                            >
                              Create API Connection
                            </button>
                          </Tab>
                        </Tabs>
                      </div>
                    )}
                  </div>
                ))}{" "}
              </div>
            </div>
          </Tab>
        )}
        {end}
      </Tabs>
      {/* <div>
        <JsonCollapse defaulPlainText json={importedJSON} label="Item" />
      </div>
      <div>
        <JsonCollapse defaulPlainText json={apiProfiles} label="Api Profiles" />
      </div> */}
    </div>
  );
}
